import React, { useEffect } from 'react';
import { Link, Outlet, Route, useLocation, useNavigate } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Seo } from './components/Seo';
import { Container } from './components/Primitives';
import { LANGS, LEGACY_HASHES, PATHS, SERVICE_KEYS } from './routes';
import { useSite } from './useSite';
import HomePage from './pages/HomePage';
import PlansPage from './pages/PlansPage';
import ReviewPage from './pages/ReviewPage';
import { WhatsAppButton } from './components/WhatsAppButton';
import { AudiencePage, ExpertisePage, ServicePage } from './pages/DetailPages';
import { AboutPage, BookPage, HowWeWorkPage, InsightsPage, LegalPage } from './pages/FirmPages';

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const go = () => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const timers = [100, 500, 1200].map((ms) => setTimeout(go, ms));
    return () => timers.forEach(clearTimeout);
  }, [pathname, hash]);
  return null;
};

export const SiteLayout = () => {
  const { lang } = useSite();
  return (
    <div lang={lang} className="flex min-h-screen flex-col bg-bone-light font-sans text-cafe-noir antialiased">
      <ScrollManager />
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export const RootRedirect = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  useEffect(() => {
    navigate(LEGACY_HASHES[hash] || PATHS.home.en, { replace: true });
  }, [hash, navigate]);
  return null;
};

export const NotFound = () => {
  const { lang } = useSite();
  return (
    <Container className="py-32">
      <Seo title="404 | Bullish Accounting" description="Page not found." noindex />
      <h1 className="font-serif text-5xl text-kombu-green">404</h1>
      <Link to={PATHS.home[lang]} className="mt-6 inline-block text-kombu-green underline" data-testid="not-found-home-link">BULLISH — Home</Link>
    </Container>
  );
};

const elementFor = (key) => {
  if (key === 'home') return <HomePage />;
  if (key === 'investors' || key === 'owners') return <AudiencePage audience={key} />;
  if (SERVICE_KEYS.includes(key)) return <ServicePage service={key} />;
  if (['privacy', 'terms', 'disclaimer'].includes(key)) return <LegalPage doc={key} />;
  return { expertise: <ExpertisePage />, howWeWork: <HowWeWorkPage />, about: <AboutPage />, insights: <InsightsPage />, book: <BookPage />, plans: <PlansPage />, review: <ReviewPage /> }[key];
};

export const siteRoutes = () => (
  <Route element={<SiteLayout />}>
    {Object.keys(PATHS).flatMap((key) =>
      LANGS.map((lang) => <Route key={`${key}-${lang}`} path={PATHS[key][lang]} element={elementFor(key)} />),
    )}
    <Route path="*" element={<NotFound />} />
  </Route>
);
